// Framework-free SSE parser for the assistant stream. The network layer (fetch +
// ReadableStream, see chat-client.ts) hands us decoded text chunks of arbitrary
// size; a single chunk may contain zero, one, or many events, and an event may
// be split across chunks anywhere — mid-line or mid-JSON. This parser buffers
// until it sees an event terminator (a blank line) and only then emits.
//
// We follow the SSE framing from the WHATWG spec closely enough for our needs:
//   - lines are separated by \n, \r\n, or \r
//   - a `field: value` line; a leading space after the colon is stripped
//   - `data:` lines accumulate, joined by \n
//   - `event:` sets the event type
//   - a blank line dispatches the buffered event
//   - lines starting with `:` are comments (ignored — used for keepalive pings)
//
// We do NOT support the full spec (id / retry fields) — the assistant contract
// doesn't use them. Unknown event types are surfaced as errors rather than
// silently dropped, so a contract drift is visible in tests.

import type { AssistantEvent } from './types.ts';

const KNOWN_TYPES = new Set(['text', 'tool', 'batch', 'applied', 'done', 'error']);

export interface ParsedFrame {
  event: string;
  data: string;
}

/**
 * Incremental SSE frame reader. Feed it decoded text; it returns the frames
 * completed by that feed. Carries an internal buffer across calls so events
 * split across chunk boundaries reassemble correctly.
 */
export class SseFrameReader {
  private buffer = '';

  /** Push a decoded text chunk, get back any frames it completed. */
  push(chunk: string): ParsedFrame[] {
    this.buffer += chunk;
    const frames: ParsedFrame[] = [];

    // A frame ends at a blank line. Normalize CRLF/CR so we can split on \n\n.
    // We only consume up to the last complete terminator; the remainder (a
    // partial frame) stays buffered for the next push.
    this.buffer = this.buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    let sep: number;
    while ((sep = this.buffer.indexOf('\n\n')) !== -1) {
      const raw = this.buffer.slice(0, sep);
      this.buffer = this.buffer.slice(sep + 2);
      const frame = parseFrameBlock(raw);
      if (frame) frames.push(frame);
    }
    return frames;
  }

  /** Flush a trailing frame that wasn't terminated by a blank line (stream end
   *  without a final \n\n). Returns it if the buffer holds a usable frame. */
  flush(): ParsedFrame[] {
    const rest = this.buffer.trim();
    this.buffer = '';
    if (!rest) return [];
    const frame = parseFrameBlock(rest);
    return frame ? [frame] : [];
  }
}

/** Parse one raw frame block (lines between blank-line separators). */
function parseFrameBlock(block: string): ParsedFrame | null {
  let event = 'message';
  const dataLines: string[] = [];
  let sawField = false;

  for (const line of block.split('\n')) {
    if (line === '' || line.startsWith(':')) continue; // blank / comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1); // strip one leading space
    if (field === 'event') {
      event = value;
      sawField = true;
    } else if (field === 'data') {
      dataLines.push(value);
      sawField = true;
    }
    // id / retry / unknown fields ignored.
  }

  if (!sawField) return null; // whitespace-only block
  return { event, data: dataLines.join('\n') };
}

/**
 * Turn a raw frame into a typed AssistantEvent. Invalid JSON or an unknown
 * event type is reported as a synthetic error event (the caller renders it as a
 * stream error rather than crashing).
 */
export function toAssistantEvent(frame: ParsedFrame): AssistantEvent {
  let payload: unknown;
  try {
    payload = frame.data === '' ? {} : JSON.parse(frame.data);
  } catch {
    return {
      type: 'error',
      data: { code: 'bad_frame', message: `Malformed SSE data for event "${frame.event}"` },
    };
  }

  if (!KNOWN_TYPES.has(frame.event)) {
    return {
      type: 'error',
      data: { code: 'unknown_event', message: `Unknown SSE event "${frame.event}"` },
    };
  }

  // Trust the contract shape; the fields are validated by usage downstream.
  return { type: frame.event as AssistantEvent['type'], data: payload } as AssistantEvent;
}

/**
 * Convenience: a stateful parser that maps decoded text chunks straight to
 * typed events. Combines SseFrameReader + toAssistantEvent.
 */
export class AssistantSseParser {
  private reader = new SseFrameReader();

  push(chunk: string): AssistantEvent[] {
    return this.reader.push(chunk).map(toAssistantEvent);
  }

  flush(): AssistantEvent[] {
    return this.reader.flush().map(toAssistantEvent);
  }
}
